import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover'
import { cn } from '@/shared/lib/utils'
import { AIShotGenerationCharacterVariantSelection, Character, CharacterVariant } from '@storyverse/shared'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'

type ShotCharacterVariantPickerProps = {
    characters: Character[]
    selected: AIShotGenerationCharacterVariantSelection[]
    onChange: (next: AIShotGenerationCharacterVariantSelection[]) => void
    maxSelections: number
}

type VariantOption = AIShotGenerationCharacterVariantSelection & {
    searchText: string
}

const buildLookSummary = (_character: Character, variant: CharacterVariant) => {
    const details = [
        variant.role,
        variant.age ? `${variant.age} years old` : '',
        variant.hair,
        variant.clothing_style,
        variant.current_emotional_state,
    ].filter(Boolean)

    return details.join(', ')
}

export default function ShotCharacterVariantPicker({
    characters,
    selected,
    onChange,
    maxSelections,
}: ShotCharacterVariantPickerProps) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')

    const options = useMemo<VariantOption[]>(() => {
        return characters.flatMap((character) =>
            (character.variants || []).map((variant) => ({
                characterId: character.id,
                characterName: character.fullname,
                variantId: variant.id,
                variantLabel: variant.label,
                role: variant.role,
                age: variant.age,
                look: buildLookSummary(character, variant),
                searchText: `${character.fullname} ${variant.label} ${variant.role || ''} ${variant.age || ''}`.toLowerCase(),
            }))
        )
    }, [characters])

    const filteredOptions = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase()
        if (!normalizedQuery) return options
        return options.filter((option) => option.searchText.includes(normalizedQuery))
    }, [options, query])

    const selectedKeys = new Set(selected.map((item) => `${item.characterId}:${item.variantId}`))

    const toggleSelection = (option: AIShotGenerationCharacterVariantSelection) => {
        const key = `${option.characterId}:${option.variantId}`
        const isSelected = selectedKeys.has(key)

        if (isSelected) {
            onChange(selected.filter((item) => `${item.characterId}:${item.variantId}` !== key))
            return
        }

        if (selected.length >= maxSelections) return
        onChange([...selected, option])
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium">Characters and Variants</p>
                <span className="text-xs text-muted-foreground">
                    {selected.length}/{maxSelections} selected
                </span>
            </div>

            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                        <span className="truncate text-left">
                            {selected.length > 0
                                ? 'Add or manage selected character variants'
                                : 'Search characters and variants'}
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[420px] p-0">
                    <div className="border-b p-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search by character, variant, role..."
                                className="pl-9"
                            />
                        </div>
                    </div>
                    <div className="max-h-80 overflow-y-auto p-2">
                        {filteredOptions.length === 0 ? (
                            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                                No matching character variants found.
                            </p>
                        ) : (
                            <div className="space-y-1">
                                {filteredOptions.map((option) => {
                                    const key = `${option.characterId}:${option.variantId}`
                                    const isSelected = selectedKeys.has(key)
                                    const isDisabled = !isSelected && selected.length >= maxSelections

                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            className={cn(
                                                'flex w-full items-start justify-between rounded-md px-3 py-2 text-left transition-colors',
                                                isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
                                                isDisabled && 'cursor-not-allowed opacity-50'
                                            )}
                                            onClick={() => toggleSelection(option)}
                                            disabled={isDisabled}
                                        >
                                            <div className="min-w-0">
                                                <p className="truncate text-sm font-medium">
                                                    {option.characterName}
                                                </p>
                                                <p className="truncate text-xs text-muted-foreground">
                                                    {option.variantLabel}
                                                    {option.look ? ` - ${option.look}` : ''}
                                                </p>
                                            </div>
                                            <Check className={cn('mt-0.5 h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                                        </button>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>

            {selected.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {selected.map((item) => (
                        <div
                            key={`${item.characterId}:${item.variantId}`}
                            className="inline-flex items-center gap-2 rounded-full border bg-muted px-3 py-1 text-xs"
                        >
                            <span className="font-medium">{item.characterName}</span>
                            <span className="text-muted-foreground">/ {item.variantLabel}</span>
                            <button
                                type="button"
                                onClick={() =>
                                    onChange(
                                        selected.filter(
                                            (selectedItem) =>
                                                !(
                                                    selectedItem.characterId === item.characterId &&
                                                    selectedItem.variantId === item.variantId
                                                )
                                        )
                                    )
                                }
                                className="text-muted-foreground transition-colors hover:text-foreground"
                                aria-label={`Remove ${item.characterName} ${item.variantLabel}`}
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
